import type { InputWithOem } from "../../types/inputs";
import type { OrbitFileStatus } from "../../types/orbits";
import { buildUrl, fetchWithAuth, safeJson } from "../helpers/api";
import {
  columnFiltersToBackendString,
  type ColumnFiltersState,
  type PagingParams,
  sortingStateToBackendParams,
} from "../helpers/pager";
import { InputsState, type InputsStateType } from "../../types/inputs";

type OrbitStatusValue =
  | OrbitFileStatus
  | string
  | { value?: OrbitFileStatus | string }
  | null
  | undefined;

export type InputOrbitRow = Omit<InputWithOem, "linkedOEM" | "status"> & {
  linkedOEM?: InputWithOem["linkedOEM"] | null;
  status?: OrbitStatusValue;
};

export interface InputOrbitPageResponse {
  content: InputOrbitRow[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first?: boolean;
  last?: boolean;
}

const INPUTS_API_PATH = "/api/v1/inputs";
export const INPUT_STATE_FILTER_ID = "__inputState";

const padDatePart = (value: number) => String(value).padStart(2, "0");

const formatSqlDayStart = (date: Date) =>
  `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(
    date.getDate(),
  )} 00:00:00`;

const getTodaySqlRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(end.getDate() + 1);

  return `${formatSqlDayStart(start)},${formatSqlDayStart(end)}`;
};

const getTomorrowSqlDayStart = () => {
  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return formatSqlDayStart(tomorrow);
};

const getTodaySqlDayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return formatSqlDayStart(today);
};

const getInputStateFilter = (state: InputsStateType) => {
  if (state === InputsState.ABOUT_TO_EXPIRE) {
    return `current equals true;linkedOEM.category.value equals "OPER";linkedOEM.endDate.value inRange ${getTodaySqlRange()}`;
  }

  const filters: Record<
    Exclude<InputsStateType, typeof InputsState.ABOUT_TO_EXPIRE>,
    string
  > = {
  [InputsState.ACTIVE]:
    `current equals true;linkedOEM.category.value equals "OPER";linkedOEM.endDate.value greaterThan ${getTomorrowSqlDayStart()}`,
  [InputsState.EXPIRED]:
    `current equals true;linkedOEM.category.value equals "OPER";linkedOEM.endDate.value lessThan ${getTodaySqlDayStart()}`,
  [InputsState.TEST]: 'linkedOEM.category.value equals "TEST"',
  };

  return filters[state];
};

const isInputStateType = (value: unknown): value is InputsStateType =>
  Object.values(InputsState).includes(value as InputsStateType);

const buildInputsFilter = (filter?: ColumnFiltersState) => {
  if (!filter?.length) return {};

  const backendFilters: string[] = [];
  const columnFilters = filter.filter((currentFilter) => {
    if (currentFilter.id !== INPUT_STATE_FILTER_ID) return true;

    if (isInputStateType(currentFilter.value)) {
      backendFilters.push(getInputStateFilter(currentFilter.value));
    }

    return false;
  });

  const serializedColumnFilters = columnFiltersToBackendString(columnFilters);

  if (serializedColumnFilters) {
    backendFilters.push(serializedColumnFilters);
  }

  return backendFilters.length ? { filter: backendFilters.join(";") } : {};
};

const getRowsFromInputsResponse = (response: unknown): InputOrbitRow[] => {
  if (Array.isArray(response)) return response;

  if (response && typeof response === "object") {
    const page = response as {
      content?: InputOrbitRow[];
      data?: InputOrbitRow[];
      items?: InputOrbitRow[];
      results?: InputOrbitRow[];
    };

    return page.content ?? page.data ?? page.items ?? page.results ?? [];
  }

  return [];
};

const getCountFromInputsResponse = (response: unknown): number | undefined => {
  if (Array.isArray(response)) return response.length;

  if (response && typeof response === "object") {
    const page = response as {
      totalElements?: number;
      total?: number;
      count?: number;
      numberOfElements?: number;
      content?: InputOrbitRow[];
      data?: InputOrbitRow[];
      items?: InputOrbitRow[];
      results?: InputOrbitRow[];
    };

    return (
      page.totalElements ??
      page.total ??
      page.count ??
      page.numberOfElements ??
      getRowsFromInputsResponse(page).length
    );
  }

  return undefined;
};

const normalizeInputsPageResponse = (
  response: unknown,
): InputOrbitPageResponse => {
  const content = getRowsFromInputsResponse(response);
  const totalElements = getCountFromInputsResponse(response) ?? content.length;

  if (response && typeof response === "object" && !Array.isArray(response)) {
    const page = response as Partial<InputOrbitPageResponse>;

    return {
      content,
      page: page.page ?? 0,
      size: page.size ?? content.length,
      totalElements,
      totalPages:
        page.totalPages ??
        (page.size ? Math.ceil(totalElements / page.size) : 1),
      first: page.first,
      last: page.last,
    };
  }

  return {
    content,
    page: 0,
    size: content.length,
    totalElements,
    totalPages: 1,
    first: true,
    last: true,
  };
};

export const getInputOrbitFilesPageResponse = async ({
  page,
  size,
  filter,
  sort,
}: PagingParams): Promise<InputOrbitPageResponse> => {
  const url = buildUrl(INPUTS_API_PATH, {
    pageSize: size,
    page,
    ...buildInputsFilter(filter),
    ...sortingStateToBackendParams(sort),
  });

  const res = await fetchWithAuth(url);
  return normalizeInputsPageResponse(await safeJson(res));
};

export const getInputOrbitFilesPage = async (params: PagingParams) =>
  (await getInputOrbitFilesPageResponse(params)).content;

export const getInputOrbitFilesCount = async ({
  filter,
}: {
  filter?: ColumnFiltersState;
}) => {
  try {
    const response = await getInputOrbitFilesPageResponse({
      page: 0,
      size: 1,
      filter,
    });

    return response.totalElements;
  } catch (error) {
    console.warn("Unable to fetch input orbit file count", error);
    return 0;
  }
};
